import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ClipboardList,
  Mail,
  UserCheck,
  Sparkles,
  Plus,
  HandHelping,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { ListEmpty, ListFooter, ListPagination, ListToolbar, Segments, ToolbarGrow } from '../ui/ListToolbar';
import { Spinner } from '../ui/Spinner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActivityLogEntry {
  id: string;
  ticket_id: string;
  actor_type: string;
  action: string;
  details: Record<string, unknown>;
  created_at: string;
  ticket: { id: string; sender_name: string | null; sender_email: string | null } | null;
  actor: { full_name: string } | null;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const ACTION_CONFIG: Record<string, { label: string; className: string; Icon: typeof Mail }> = {
  ticket_created_from_webhook: { label: 'Ticket reçu',    className: 'bg-blue-50 text-blue-700 border-blue-200',     Icon: Plus as any },
  ai_classified:               { label: 'Classifié IA',   className: 'bg-purple-50 text-purple-700 border-purple-200', Icon: Sparkles as any },
  assigned:                    { label: 'Assigné',        className: 'bg-orange-50 text-orange-700 border-orange-200', Icon: UserCheck as any },
  claimed:                     { label: 'Pris en charge', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: HandHelping as any },
  status_changed:              { label: 'Statut modifié', className: 'bg-neutral-100 text-neutral-600 border-neutral-200', Icon: RefreshCw as any },
  email_sent:                  { label: 'Email envoyé',   className: 'bg-teal-50 text-teal-700 border-teal-200',      Icon: Mail as any },
  notifications_sent:          { label: 'Email envoyé',   className: 'bg-teal-50 text-teal-700 border-teal-200',      Icon: Mail as any },
};

const FILTERS: { value: string; label: string }[] = [
  { value: 'all',                        label: 'Tous' },
  { value: 'ticket_created_from_webhook', label: 'Demandes reçues' },
  { value: 'ai_classified',              label: 'Classifié IA' },
  { value: 'assigned',                   label: 'Assignations' },
  { value: 'claimed',                    label: 'Prises en charge' },
  { value: 'email_sent',                 label: 'Emails' },
  { value: 'status_changed',             label: 'Statuts' },
];

const PAGE_SIZE = 25;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function actionConfig(action: string) {
  return ACTION_CONFIG[action] ?? { label: action, className: 'bg-neutral-100 text-neutral-600 border-neutral-200', Icon: ClipboardList };
}

function detailsSummary(action: string, details: Record<string, unknown>): string {
  switch (action) {
    case 'ticket_created_from_webhook':
      return details.form_id ? `via formulaire ${details.form_id}` : 'formulaire web';
    case 'ai_classified':
      return details.category
        ? `→ ${details.category}${details.confidence ? ` (${Math.round(Number(details.confidence) * 100)}%)` : ''}`
        : '';
    case 'assigned':
      return details.assigned_to_name ? `→ ${details.assigned_to_name}` : '';
    case 'claimed':
      return details.claimed_by_name ? `par ${details.claimed_by_name}` : '';
    case 'status_changed':
      return details.to ? `→ ${details.to}` : '';
    case 'email_sent':
    case 'notifications_sent': {
      const emails = details.recipient_emails as string[] | undefined;
      if (emails?.length) return `→ ${emails.join(', ')}`;
      return details.reason ? String(details.reason) : '';
    }
    default:
      return '';
  }
}

// ─── Service ──────────────────────────────────────────────────────────────────

async function getGlobalActivityLog(actionFilter: string, page: number) {
  let query = supabase
    .from('ticket_activity_log')
    .select(
      `id, ticket_id, actor_type, action, details, created_at,
       ticket:tickets(id, sender_name, sender_email),
       actor:collaborators(full_name)`,
      { count: 'exact' },
    )
    .order('created_at', { ascending: false })
    .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

  if (actionFilter !== 'all') {
    if (actionFilter === 'email_sent') {
      query = query.in('action', ['email_sent', 'notifications_sent']);
    } else {
      query = query.eq('action', actionFilter);
    }
  }

  const { data, error, count } = await query;
  if (error) throw error;
  return { entries: (data ?? []) as unknown as ActivityLogEntry[], total: count ?? 0 };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function LogsPage() {
  const [actionFilter, setActionFilter] = useState('all');
  const [page, setPage] = useState(0);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['activity-logs', actionFilter, page],
    queryFn: () => getGlobalActivityLog(actionFilter, page),
    refetchInterval: 30_000,
  });

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  function handleFilterChange(val: string) {
    setActionFilter(val);
    setPage(0);
  }

  return (
    <div className="main-container-gdm w-full space-y-3">
      <ListToolbar>
        <Segments label="Type d'événement" value={actionFilter} onChange={handleFilterChange} options={FILTERS} />
        <ToolbarGrow />
        <Button variant="secondary" onClick={() => refetch()}>
          <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} />
          Actualiser
        </Button>
      </ListToolbar>

      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="py-16 text-center text-red-600 text-sm flex flex-col items-center gap-2">
            <AlertCircle size={20} />
            Erreur de chargement des logs
          </div>
        ) : entries.length === 0 ? (
          <ListEmpty>Aucun événement pour ce filtre</ListEmpty>
        ) : (
          <table className="cime-table text-[13px]">
            <thead>
              <tr className="text-left">
                <th className="py-2 pl-[18px] pr-2.5">Événement</th>
                <th className="px-2.5 py-2">Demande</th>
                <th className="w-52 px-2.5 py-2">Acteur</th>
                <th className="w-44 py-2 pl-2.5 pr-[18px]">Date</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const cfg = actionConfig(entry.action);
                const summary = detailsSummary(entry.action, entry.details);
                return (
                  <tr key={entry.id}>
                    <td className="max-w-md py-[9px] pl-[18px] pr-2.5">
                      <span className={cn(
                        'inline-flex h-[22px] items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium',
                        cfg.className,
                      )}>
                        <cfg.Icon size={11} aria-hidden />
                        {cfg.label}
                      </span>
                      {summary ? (
                        <span className="mt-1 block truncate text-xs text-neutral-400">{summary}</span>
                      ) : null}
                    </td>
                    <td className="px-2.5 py-[9px]">
                      {entry.ticket ? (
                        <Link
                          to={`/tickets/${entry.ticket_id}`}
                          className="font-medium text-cime-primary hover:underline"
                        >
                          {entry.ticket.sender_name ?? entry.ticket.sender_email ?? entry.ticket_id.slice(0, 8)}
                        </Link>
                      ) : (
                        <span className="text-neutral-300">—</span>
                      )}
                    </td>
                    <td className="px-2.5 py-[9px] text-cime-text-muted">
                      {entry.actor?.full_name ?? (entry.actor_type === 'system' ? (
                        <span className="text-xs italic text-neutral-400">Système</span>
                      ) : <span className="text-neutral-300">—</span>)}
                    </td>
                    <td className="whitespace-nowrap py-[9px] pl-2.5 pr-[18px] tabular-nums text-cime-text-muted">
                      {format(new Date(entry.created_at), 'd MMM yyyy · HH:mm', { locale: fr })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {totalPages > 1 ? (
          <ListFooter pagination={<ListPagination page={page} pageCount={totalPages} onChange={setPage} />}>
            Page {page + 1} sur {totalPages} · {total} événements
          </ListFooter>
        ) : null}
      </div>
    </div>
  );
}
